import { Stack, Typography, Button, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { TableCellCustom } from '@app/components/table';
import { IInterviewee } from '../types';
import { useDialog, useRequestState } from '@app/hooks';
import { RowActions } from './row-actions';
import { CustomAvatar } from '@app/components/custom-avatar';
import {
  ConfirmationDialog,
  FormProvider,
  RHFSelect,
  StatusTag,
  TruncatedTypography,
  formatTimeSlots,
} from '@app/components';
import { selectEmployeesState, getEmployees } from '@app/modules/employees';
import { IntervieweeStatus, Strings } from '@app/constants';
import { updateIntervieweeStatus } from '../slice';
import { selectUpdateInterviweeStatusState } from '../selecter';
import { updateIntervieweeStatusSchema } from '../validators';
import { selectCandidateState } from '@app/modules/candidates/selectors';
import { getCandidates } from '@app/modules/candidates/slice';
import { AttachmentViewer } from '@app/components/card';
interface Props {
  row: IInterviewee;
  selected: boolean;
  onSelectRow: VoidFunction;
  isManager: boolean;
}
const IntervieweeRow = (props: Props) => {
  const { row, isManager } = props;
  const methods = useForm({
    resolver: yupResolver(updateIntervieweeStatusSchema) as any,
  });
  const confirmDialog = useDialog();
  const viewResume = useDialog();
  const dispatch = useDispatch();

  const array: string[] = Array.isArray(row?.interviewTime)
    ? row?.interviewTime
    : ([row?.interviewTime].filter(Boolean) as string[]);

  const interviewTime = formatTimeSlots(array);

  const handleClick = () => {
    viewResume.show();
  };

  useEffect(() => {
    dispatch(getEmployees());
    dispatch(getCandidates());
  }, [dispatch]);
  const { data } = useRequestState({
    stateSelector: selectCandidateState,
  });

  const resume = data?.find((candidate) => candidate.email === row.candidateEmail)?.resume;

  const managerState = useSelector(selectEmployeesState);
  const manager = managerState.data?.records;

  const updatedStatus = useWatch({ control: methods.control, name: 'updateStatus' });

  const onSubmit = (reason: string) => {
    dispatch(updateIntervieweeStatus({ id: row?.id, status: updatedStatus, reason }));
  };

  const { loading } = useRequestState({
    stateSelector: selectUpdateInterviweeStatusState,
    onSuccess: confirmDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });

  const statusOptions = [
    { value: 'InterviewScheduled', label: Strings.status.InterviewScheduled },
    { value: 'InterviewCancelled', label: Strings.status.InterviewCancelled },
    { value: 'InProgress', label: Strings.status.inProgress },
    { value: 'DecisionPending', label: Strings.status.decisionPending },
    { value: 'Rejected', label: Strings.status.rejected },
    { value: 'Selected', label: Strings.status.Selected },
  ];
  const statusMapping: Record<string, IntervieweeStatus> = {
    InterviewScheduled: IntervieweeStatus.InterviewScheduled,
    InterviewCancelled: IntervieweeStatus.InterviewCancelled,
    InProgress: IntervieweeStatus.InProgress,
    DecisionPending: IntervieweeStatus.DecisionPending,
    Rejected: IntervieweeStatus.Rejected,
    Selected: IntervieweeStatus.Selected,
  };
  const statusValue = statusMapping[row.status] || IntervieweeStatus.InterviewScheduled;

  return (
    <>
      <TableCellCustom flex>
        <Typography variant="body2">{row.id}</Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center">
        <Stack style={{ flexDirection: 'row', alignItems: 'center', width: '150px', gap: 10 }}>
          <CustomAvatar name={row?.candidateName} color="info" />
          <Typography variant="body2">{row?.candidateName}</Typography>
        </Stack>
      </TableCellCustom>
      <TableCellCustom flex align="center">
        <Typography variant="body2">{row?.candidateEmail}</Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center">
        <Typography variant="body2" sx={{ width: '200px' }}>
          {interviewTime ?? 'NA'}
        </Typography>
      </TableCellCustom>
      <TableCellCustom flex>
        {manager?.map((option, index) => (
          <Stack key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: '9px' }}>
            {row.interviewer === option.id && (
              <CustomAvatar name={`${option.firstName}${option.lastName}`} color="info" />
            )}
            <Typography variant="body2" key={index} width={'60px'}>
              {row.interviewer === option.id ? option.firstName : ''}
            </Typography>
          </Stack>
        ))}
      </TableCellCustom>
      <TableCellCustom>
        <Button onClick={handleClick} sx={{ width: '90px' }}>
          {Strings.button.clickHere}
        </Button>
      </TableCellCustom>
      <TableCellCustom flex align="center">
        <FormProvider methods={methods}>
          <RHFSelect
            name="updateStatus"
            defaultValue={'Draft'}
            value={statusValue}
            sx={{ width: '120px' }}
            SelectProps={{
              sx: {
                fontSize: '14px',
              },
            }}
            size="small"
            onChange={(e) => {
              methods.setValue('updateStatus', e.target.value);
              methods.setValue('reason', '');
              confirmDialog.show();
            }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>
        </FormProvider>
      </TableCellCustom>
      <FormProvider methods={methods}>
        <ConfirmationDialog
          title={`${Strings.common.update} ${Strings.field.status}`}
          loading={loading}
          confirmationButtonTitle={Strings.button.save}
          caption={Strings.common.updateCaption(updatedStatus)}
          visible={confirmDialog.visible}
          onClickConfirm={onSubmit}
          onClose={confirmDialog.hide}
        />
      </FormProvider>
      <TableCellCustom width={50} align="center">
        <StatusTag status={IntervieweeStatus[statusValue]} />
      </TableCellCustom>
      <TableCellCustom flex align="center">
        <TruncatedTypography width={'200px'} text={row?.reason ?? 'NA'} />
      </TableCellCustom>
      {isManager ? undefined : (
        <TableCellCustom width={50} align="right">
          <RowActions row={row} />
        </TableCellCustom>
      )}

      {viewResume.visible && <AttachmentViewer open onClose={viewResume.hide} resume={resume} />}
    </>
  );
};

export { IntervieweeRow };
